import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'kbong_auth_token';

// Global axios defaults for performance
axios.defaults.timeout = 25000; // 25s timeout for all requests

// Set axios default Authorization header from stored token
function setAxiosToken(token) {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleConfig, setRoleConfig] = useState(null);

  const fetchRoleConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/my-permissions`);
      setRoleConfig(res.data);
    } catch {
      setRoleConfig(null);
    }
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    // Apply token to all future requests before calling /auth/me
    setAxiosToken(token);
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (err) {
      // Only clear token on explicit 401/403 (auth failure), not on network/timeout errors
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem(TOKEN_KEY);
        setAxiosToken(null);
        setUser(null);
      }
      // For 429/504/network errors, keep the token — it may still be valid
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      fetchRoleConfig();
    } else {
      setRoleConfig(null);
    }
  }, [user, fetchRoleConfig]);

  const login = (userData, token) => {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      setAxiosToken(token);
    }
    setUser(userData);
  };

  const logout = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      setAxiosToken(null);
      setUser(null);
      setRoleConfig(null);
    }
  };

  // Role-based nav access — never defaults to true for unknown state
  const hasNavAccess = (path) => {
    if (!user) return false;
    const navAccess = roleConfig?.nav_access || user?.nav_access || [];
    if (navAccess.length === 0) return true; // Allow all if no restrictions configured
    const cleanPath = path.replace(/^\//, '');
    // face-scanner is accessible to anyone with employees access
    if (cleanPath === 'face-scanner') return navAccess.includes('employees');
    // profile is always accessible to logged-in users
    if (cleanPath === 'profile') return true;
    return navAccess.includes(cleanPath);
  };

  const getUserRole = () => {
    return roleConfig?.role || user?.effective_role || user?.role || 'operator';
  };

  const canApprove = () => {
    return roleConfig?.can_approve || false;
  };

  const hasPerm = (permission) => {
    if (!user || !roleConfig) return false;
    if (roleConfig.role === 'admin') return true;
    return roleConfig?.permissions?.[permission] === true;
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, checkAuth,
      roleConfig, hasNavAccess, getUserRole, canApprove, hasPerm
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
